/**
 * Thin typed wrappers around the /store REST endpoints. No rendering or
 * purchase-eligibility logic lives here — see shop-screen.ts for that
 * (same split as colony-api.ts / colony-screen.ts).
 *
 * Pillar (mirrors backend/src/game/store/store.catalog.ts): every purchase
 * spends Delta-V (premium currency) on time, material or style — never combat
 * power.
 */
import { api } from '../../core/network/api';
import type { BuildingType, UnitType } from '../colony/colony-api';

export type TimeSkipKind = 'building' | 'training' | 'research';

/**
 * Only Delta-V. The template's economy is dual-currency, but this game has no
 * soft currency — nothing grants it and nothing spends it, so the server stopped
 * returning it rather than have the shop display a balance under an invented
 * name.
 */
export interface StoreBalance {
  premium: number;
}

/**
 * Mirrors backend TITLE_SKUS — the 51 premium entries of the title catalog.
 * `rarity` drives the card's tint and the section grouping; the shop never
 * reports ownership (see `fetchTitles` in the profile screen's api, which is
 * the single source of owned-state for both surfaces).
 */
export interface TitleSku {
  id: string;
  name: string;
  rarity: 'COMMON' | 'RARE' | 'EPIC' | 'LEGENDARY';
  price: number;
}

/** Mirrors backend DELTA_V_PACKS — the one catalog entry that CREDITS premium currency (real money -> Delta-V) rather than spending it. */
export interface DeltaVPackSku {
  id: string;
  label: string;
  deltaV: number;
  priceCents: number;
}

/** Mirrors backend RESOURCE_DELIVERY_SKUS — Delta-V spent on raw resource, not time/cosmetics, a separate purchase group from everything above. */
export interface ResourceDeliverySku {
  id: string;
  resourceType: 'REGOLITH' | 'WATER_ICE' | 'HE3' | 'CRIMSON_DUST';
  label: string;
  amount: number;
  price: number;
}

/** GET /store/catalog's exact response shape — see store.service.ts#getCatalog. */
export interface StoreCatalog {
  timeSkipPrices: Record<TimeSkipKind, number>;
  titles: TitleSku[];
  deltaVPacks: DeltaVPackSku[];
  /**
   * Bounds for the custom-amount slider, from the server. The checkout panel
   * renders nothing until these arrive rather than inventing a range the
   * server would then reject.
   */
  deltaVCustomMinCents: number;
  deltaVCustomMaxCents: number;
  resourceDeliveries: ResourceDeliverySku[];
}

/**
 * Which queue to target for a time-skip purchase. `buildingType` is required
 * server-side when kind === 'building', `unitType` when kind === 'training';
 * neither is used for kind === 'research' (it single-threads the whole tech
 * tree, so there's nothing to disambiguate).
 */
export interface TimeSkipTarget {
  buildingType?: BuildingType;
  unitType?: UnitType;
}

export function fetchStoreCatalog(): Promise<StoreCatalog> {
  return api.get<StoreCatalog>('/store/catalog');
}

/** Current Delta-V balance for the calling player. */
export function fetchStoreBalance(): Promise<StoreBalance> {
  return api.get<StoreBalance>('/store/balance');
}

/**
 * Instantly finishes a queued timer. IMPORTANT: the premium debit happens
 * server-side BEFORE the skip is validated against what's actually queued —
 * a call with no matching in-progress building/unit/research is NOT refunded
 * (see store.service.ts's purchaseTimeSkip doc comment). Callers must confirm
 * a real target is in progress before invoking this.
 */
export function purchaseTimeSkip(kind: TimeSkipKind, target: TimeSkipTarget = {}): Promise<unknown> {
  return api.post<unknown>('/store/timeskip', {
    kind,
    buildingType: target.buildingType,
    unitType: target.unitType,
  });
}

/** Grants a purely cosmetic inventory item — no gameplay effect. */
export function purchaseTitle(skuId: string): Promise<{ titleId: string }> {
  return api.post<{ titleId: string }>('/store/title', { skuId });
}


/**
 * Buys a Delta-V pack with real money. CURRENTLY TEST-ONLY: no live payment
 * processor is wired up yet — the server refuses the purchase, and this
 * completes instantly with no real charge. The response doesn't carry the
 * new balance; callers must re-fetch it (see shop-screen.ts's
 * handleBuyDeltaV), same as every other purchase here.
 */
export function purchaseDeltaV(packId: string): Promise<{ checkoutId: string }> {
  return api.post<{ checkoutId: string }>('/store/delta-v', { packId });
}

/** Spends Delta-V on raw resource, credited straight to the colony's stockpile. */
export function purchaseResourceDelivery(skuId: string): Promise<unknown> {
  return api.post<unknown>('/store/resource-delivery', { skuId });
}

/**
 * Buys a custom amount of Delta-V.
 *
 * Sends CENTS, never a Delta-V figure: the server re-derives the grant from
 * the price it charges, so a miscalculated quote here changes what the player
 * was shown, never what they are given. The response carries the amount
 * actually granted, which is what the toast should report.
 */
export function purchaseCustomDeltaV(amountCents: number): Promise<{ checkoutId: string; deltaV: number }> {
  return api.post<{ checkoutId: string; deltaV: number }>('/store/delta-v/custom', { amountCents });
}

/**
 * Client-side quote for the slider, mirroring backend `deltaVForCents`.
 *
 * Duplicated deliberately rather than round-tripping on every drag: the slider
 * has to label itself while the player moves it. The server never trusts this
 * value — it is display only.
 */
export function quoteDeltaVForCents(cents: number, packs: DeltaVPackSku[], minCents: number, maxCents: number): number {
  if (packs.length === 0) return 0;
  const clamped = Math.min(Math.max(Math.floor(cents), minCents), maxCents);
  const sorted = [...packs].sort((a, b) => a.priceCents - b.priceCents);
  const rung = sorted.reduce((best, pack) => (pack.priceCents <= clamped ? pack : best), sorted[0]);
  return Math.floor((clamped * rung.deltaV) / rung.priceCents);
}


// ─── special offers & boosts ──────────────────────────────────────────────────────────────────────
// Mirrors backend game/offer (offer.service.ts OfferView). Prices are Delta-V at the shop's reference
// rate of 100 per EUR, so 499 is "4.99".

export type BoostType = 'BUILD_SPEED' | 'TRAIN_SPEED' | 'MINING' | 'DEFENCE' | 'ATTACK' | 'RESEARCH';

export interface BoostParams {
  pct?: number;
  otherPct?: number;
  resource?: string;
  speedPct?: number;
  costPct?: number;
}

export interface OfferView {
  id: string;
  kind: 'BOOST' | 'TITLE' | 'RESOURCE_PACK';
  /** The shop page it is surfaced on besides Special Offers. */
  page: 'skip-time' | 'resources' | 'titles' | 'offers';
  art: string;
  priceDv: number;
  originalDv: number;
  onSale: boolean;
  usesLeft: number;
  maxUses: number;
  /** A purchase of this offer is already running (ISO end time). */
  activeUntil: string | null;
  boost: { type: BoostType; params: BoostParams; durationMs: number; uses: number | null } | null;
  pack: { resource: string; amount: number } | null;
}

export interface OffersResponse {
  windowIndex: number;
  windowEndsAt: string;
  now: string;
  offers: OfferView[];
}

export interface BoostView {
  id: string;
  type: BoostType;
  offerId: string;
  params: BoostParams;
  startsAt: string;
  endsAt: string;
  /** Null for pure time boosts; battles left for an attack boost. */
  usesRemaining: number | null;
}

export function fetchOffers(): Promise<OffersResponse> {
  return api.get<OffersResponse>('/me/offers');
}

export function fetchBoosts(): Promise<BoostView[]> {
  return api.get<BoostView[]>('/me/boosts');
}

export function purchaseOffer(offerId: string): Promise<{ offerId: string; priceDv: number; grantedTitleId?: string }> {
  return api.post('/store/offer', { offerId });
}
